import { ListingsV3QueryProps, PublicListingV3 } from 'types/listingV3';
import useSWRInfinite, { SWRInfiniteKeyLoader } from 'swr/infinite';
import { get } from 'utils/axios';
import type { PaginatedResponse } from '@kouto/types';
import { useBrandId } from 'AppProvider';
import { useMemo } from 'react';
import buildListingsV3Url from './buildListingsV3Url';

const buildSWRInfiniteKey = <T extends PaginatedResponse<PublicListingV3>>(
  url?: string | null,
  brandId?: string,
): SWRInfiniteKeyLoader<T> => {
  return (index: number, previousPageData: T | null) => {
    if (
      !url ||
      !brandId ||
      (previousPageData?.meta &&
        previousPageData.meta.currentPage >=
          (previousPageData.meta.totalPages ?? 0))
    ) {
      return null;
    }

    const [base, query] = url.split('?');
    const params = new URLSearchParams(query || '');
    params.set('page', (index + 1).toString());
    params.set('brandId', brandId);
    return `${base}?${params}`;
  };
};

const useListingsV3 = (params: ListingsV3QueryProps & { skip?: boolean }) => {
  const url = params?.skip ? null : buildListingsV3Url(params);
  const brandId = useBrandId();

  const swr = useSWRInfinite<PaginatedResponse<PublicListingV3>>(
    buildSWRInfiniteKey(url, brandId),
    get,
    {
      onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
        if (retryCount >= 3) return; // Stop retrying after 3 attempts
        if (error.status < 500) return; // Only retry if it's a server-side (5xx) error
        setTimeout(() => revalidate({ retryCount }), 2 ** retryCount * 1000); // Exponential backoff
      },
    },
  );

  const items: PublicListingV3[] = useMemo(
    () => swr.data?.flatMap((item) => item.items) ?? [],
    [swr.data],
  );
  const isError = swr.error !== undefined;

  const lastPageData = swr.data?.[swr.data.length - 1];
  const hasMorePages = lastPageData?.meta
    ? lastPageData.meta.currentPage < (lastPageData.meta.totalPages ?? 0)
    : false;

  const canLoadMore = hasMorePages && !swr.isLoading && !swr.isValidating;

  const loadMore = () => {
    if (!isError && canLoadMore) {
      swr.setSize(swr.size + 1);
    }
  };

  return {
    ...swr,
    listings: items,
    loadMore,
    isError,
  };
};

export default useListingsV3;
